import React from 'react'
import { Link, useLocation } from 'react-router-dom'

/**
 * Origin context that pages can pass via React Router `state` when linking
 * into a detail view, so the destination can render a "back to ..." link
 * pointing at the originating page (workspace, scenario, etc.) instead of
 * the section default.
 *
 * Example:
 * ```
 * <Link to={`/universe/companies/${companyId}`} state={{ from: { kind: 'workspace', id: wsId, title: ws.title, path: `/workspaces/${wsId}` } }}>
 *   {company.name}
 * </Link>
 * ```
 */
export type BackOrigin = {
  kind: 'workspace' | 'scenario' | 'company' | 'item' | 'other'
  id?: string | number
  title?: string
  path?: string
}

export type BackLinkProps = {
  /** Default destination when no origin is supplied (e.g. via deep link). */
  defaultTo: string
  /** Default label to use when no origin is supplied. */
  defaultLabel: string
  /** Optional override for the location state key (defaults to `from`). */
  stateKey?: string
}

/**
 * Slim back-link shown above the page header on detail pages.
 *
 * Reads `location.state.from` (see {@link BackOrigin}) and renders
 * "← <Origin title>" when available, falling back to a section default
 * like "← Companies".
 */
export default function BackLink({ defaultTo, defaultLabel, stateKey = 'from' }: BackLinkProps) {
  const loc = useLocation()
  const from = (loc.state as Record<string, BackOrigin | undefined> | null)?.[stateKey]

  const to = from?.path || defaultTo
  const labelBase = from?.title || defaultLabel
  const truncated = labelBase.length > 60 ? labelBase.slice(0, 57) + '…' : labelBase

  return (
    <div className="back-link-row" style={{ margin: '12px 0 0' }}>
      <Link
        to={to}
        className="navlink"
        style={{
          fontSize: 13,
          color: 'var(--muted)',
          textDecoration: 'none',
        }}
      >
        <span aria-hidden="true">←</span> {truncated}
      </Link>
    </div>
  )
}
